import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  critico: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300", dot: "bg-red-500" },
  alto: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300", dot: "bg-orange-500" },
  medio: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-300", dot: "bg-yellow-500" },
  bajo: { bg: "bg-green-100", text: "text-green-800", border: "border-green-300", dot: "bg-green-500" },
};
const STATUS_COLORS = {
  abierto: "bg-blue-100 text-blue-800", en_tratamiento: "bg-orange-100 text-orange-800",
  mitigado: "bg-teal-100 text-teal-800", cerrado: "bg-gray-100 text-gray-600",
};
const STATUS_LABELS = { abierto: "Abierto", en_tratamiento: "En Tratamiento", mitigado: "Mitigado", cerrado: "Cerrado" };
const PROB_LABELS = ["muy_baja", "baja", "media", "alta", "muy_alta"];
const PROB_DISPLAY = { muy_baja: "Muy Baja", baja: "Baja", media: "Media", alta: "Alta", muy_alta: "Muy Alta" };
const IMPACT_LABELS = ["insignificante", "menor", "moderado", "mayor", "catastrofico"];
const IMPACT_DISPLAY = { insignificante: "Insignif.", menor: "Menor", moderado: "Moderado", mayor: "Mayor", catastrofico: "Catastrf." };
const IMPACT_DISPLAY_FULL = { insignificante: "Insignificante", menor: "Menor", moderado: "Moderado", mayor: "Mayor", catastrofico: "Catastrofico" };

const CELL_COLORS = [
  ["bg-green-200", "bg-green-300", "bg-yellow-200", "bg-yellow-300", "bg-orange-300"],
  ["bg-green-300", "bg-yellow-200", "bg-yellow-300", "bg-orange-300", "bg-orange-400"],
  ["bg-yellow-200", "bg-yellow-300", "bg-orange-300", "bg-orange-400", "bg-red-400"],
  ["bg-yellow-300", "bg-orange-300", "bg-orange-400", "bg-red-400", "bg-red-500"],
  ["bg-orange-300", "bg-orange-400", "bg-red-400", "bg-red-500", "bg-red-600"],
];

/* ── Heat Map ─────────────────────────────────────── */
const HeatMap = ({ matrix }) => {
  const impactLabels = [...IMPACT_LABELS].reverse();
  return (
    <div data-testid="risk-heatmap">


      <h3 className="text-sm font-semibold text-gray-700 mb-3">Mapa de Calor (Probabilidad vs Impacto)</h3>
      <div className="flex gap-1">
        {/* Y-axis label */}
        <div className="flex items-center justify-center" style={{ width: 18 }}>
          <span className="text-[10px] text-gray-400 font-semibold" style={{ writingMode: "vertical-lr", transform: "rotate(180deg)" }}>IMPACTO</span>
        </div>
        {/* Y-axis tick labels */}
        <div className="flex flex-col gap-1 justify-end" style={{ width: 72, paddingBottom: 20 }}>
          {impactLabels.map((l) => (
            <div key={l} className="h-9 flex items-center justify-end pr-1">
              <span className="text-[11px] text-gray-500 font-medium leading-tight text-right">{IMPACT_DISPLAY[l]}</span>
            </div>
          ))}
        </div>
        {/* Grid */}
        <div className="flex-1">
          {/* X-axis header */}
          <div className="flex gap-1 mb-1">
            {PROB_LABELS.map((p) => (
              <div key={p} className="flex-1 text-center text-[10px] font-medium text-gray-500 truncate">{PROB_DISPLAY[p]}</div>
            ))}
          </div>
          {/* Cells */}
          {matrix.map((row, ri) => (
            <div key={ri} className="flex gap-1 mb-1">
              {row.map((count, ci) => (
                <div key={ci} className={`flex-1 h-9 rounded flex items-center justify-center text-sm font-bold ${CELL_COLORS[ri][ci]} ${count > 0 ? "ring-2 ring-offset-1 ring-gray-400/60 text-white" : "opacity-50"}`}>
                  {count > 0 ? count : ""}
                </div>
              ))}
            </div>
          ))}
          <div className="text-center text-[10px] text-gray-400 font-semibold mt-1 tracking-widest">PROBABILIDAD</div>
        </div>
      </div>
    </div>
  );
};

/* ── Import Dialog ────────────────────────────────── */
const ImportDialog = ({ open, onClose, onImport, currentUser, selectedCompanyId }) => {
  const [vulns, setVulns] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setImportResult(null); setSelected(new Set());
    const token = localStorage.getItem("token");
    axios.get(`${API}/risks/import/available` + (currentUser?.role === "superadmin" && selectedCompanyId ? `?company_id=${selectedCompanyId}` : ""), { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setVulns(res.data.available || []))
      .catch(() => setVulns([]))
      .finally(() => setLoading(false));
  }, [open]);

  const toggle = (id) => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
  const selectAll = () => { selected.size === vulns.length ? setSelected(new Set()) : setSelected(new Set(vulns.map((v) => v.id))); };

  const handleImport = async () => {
    setImporting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/risks/import/from-scan`, { vulnerability_ids: [...selected] }, { headers: { Authorization: `Bearer ${token}` } });
      setImportResult(res.data); onImport();
    } catch { setImportResult({ success: false, message: "Error al importar" }); }
    finally { setImporting(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="import-dialog">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importar Vulnerabilidades como Riesgos</h2>
            <p className="text-sm text-gray-500 mt-0.5">Vulnerabilidades criticas/altas de sus escaneos</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-10"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
          ) : importResult ? (
            <div className={`p-4 rounded-lg text-center ${importResult.success !== false ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
              <p className="text-lg font-semibold">{importResult.message}</p>
              {importResult.imported > 0 && <p className="mt-1">Se crearon {importResult.imported} riesgos nuevos</p>}
              {importResult.skipped > 0 && <p className="text-sm mt-1">{importResult.skipped} ya estaban importados</p>}
            </div>
          ) : vulns.length === 0 ? (
            <p className="text-center text-gray-500 py-10">Todas las vulnerabilidades criticas/altas ya fueron importadas.</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <button onClick={selectAll} className="text-sm text-blue-600 hover:text-blue-800" data-testid="select-all-vulns">
                  {selected.size === vulns.length ? "Deseleccionar todo" : `Seleccionar todo (${vulns.length})`}
                </button>
                <span className="text-sm text-gray-500">{selected.size} seleccionadas</span>
              </div>
              <div className="space-y-2">
                {vulns.map((v) => (
                  <label key={v.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected.has(v.id) ? "bg-blue-50 border-blue-300" : "bg-white border-gray-200 hover:bg-gray-50"}`}>
                    <input type="checkbox" checked={selected.has(v.id)} onChange={() => toggle(v.id)} className="mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase ${v.severity === "critical" ? "bg-red-100 text-red-800" : "bg-orange-100 text-orange-800"}`}>{v.severity}</span>
                        <span className="text-sm font-medium text-gray-900 truncate">{v.title}</span>
                      </div>
                      {v.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{v.description}</p>}
                      <div className="flex gap-3 mt-1 text-xs text-gray-400">{v.category && <span>{v.category}</span>}{v.cve_id && <span>{v.cve_id}</span>}</div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50" data-testid="import-cancel">{importResult ? "Cerrar" : "Cancelar"}</button>
          {!importResult && vulns.length > 0 && (
            <button onClick={handleImport} disabled={selected.size === 0 || importing}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="import-confirm">
              {importing ? "Importando..." : `Importar ${selected.size} seleccionadas`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Risk Form Dialog ─────────────────────────────── */
const RiskFormDialog = ({ open, onClose, onSave, risk }) => {
  const [form, setForm] = useState({
    tipo: "riesgo", descripcion: "", contexto: "interno", origen: "manual",
    partes_interesadas: [], probabilidad: "media", justificacion_probabilidad: "",
    impacto: "moderado", tratamiento: "mitigar", acciones: "", responsable_nombre: "",
    fecha_objetivo: "", estado: "abierto",
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (risk) {
      setForm({ tipo: risk.tipo || "riesgo", descripcion: risk.descripcion || "", contexto: risk.contexto || "interno",
        origen: risk.origen || "manual", partes_interesadas: risk.partes_interesadas || [],
        probabilidad: risk.probabilidad || "media", justificacion_probabilidad: risk.justificacion_probabilidad || "",
        impacto: risk.impacto || "moderado", tratamiento: risk.tratamiento || "mitigar",
        acciones: risk.acciones || "", responsable_nombre: risk.responsable_nombre || "",
        fecha_objetivo: risk.fecha_objetivo ? risk.fecha_objetivo.split("T")[0] : "", estado: risk.estado || "abierto" });
    } else {
      setForm({ tipo: "riesgo", descripcion: "", contexto: "interno", origen: "manual", partes_interesadas: [],
        probabilidad: "media", justificacion_probabilidad: "", impacto: "moderado", tratamiento: "mitigar",
        acciones: "", responsable_nombre: "", fecha_objetivo: "", estado: "abierto" });
    }
  }, [risk, open]);

  const validateForm = () => {
    const e = {};
    if (!form.descripcion.trim() || form.descripcion.length < 10) {
      e.descripcion = "Descripcion obligatoria (min 10 caracteres)";
    }
    if (form.fecha_objetivo && form.fecha_objetivo < today) {
      e.fecha_objetivo = "La fecha no puede ser anterior a hoy";
    }
    if (form.tratamiento === "mitigar" && !form.responsable_nombre.trim()) {
      e.responsable_nombre = "Responsable obligatorio cuando el tratamiento es mitigar";
    }
    if (form.tratamiento === "mitigar" && !form.acciones.trim()) {
      e.acciones = "Acciones obligatorias cuando el tratamiento es mitigar";
    }
    if (["en_tratamiento", "mitigado"].includes(form.estado) && !form.responsable_nombre.trim()) {
      e.responsable_nombre = "Responsable obligatorio para este estado";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { ...form };
      if (payload.fecha_objetivo) payload.fecha_objetivo = new Date(payload.fecha_objetivo).toISOString();
      else delete payload.fecha_objetivo;
      if (typeof payload.partes_interesadas === "string") payload.partes_interesadas = payload.partes_interesadas.split(",").map(s => s.trim()).filter(Boolean);
      if (risk) await axios.put(`${API}/risks/${risk.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      else { const { estado, ...p } = payload; await axios.post(`${API}/risks`, p, { headers: { Authorization: `Bearer ${token}` } }); }
      onSave(); onClose();
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  const Sel = ({ label, field, options, displayMap }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <select value={form[field]} onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-testid={`risk-form-${field}`}>
        {options.map((o) => <option key={o} value={o}>{displayMap ? displayMap[o] : o}</option>)}
      </select>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto" data-testid="risk-form-dialog">
        <div className="p-5 border-b"><h2 className="text-lg font-semibold">{risk ? "Editar Riesgo" : "Nuevo Riesgo / Oportunidad"}</h2></div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <Sel label="Tipo" field="tipo" options={["riesgo", "oportunidad"]} displayMap={{ riesgo: "Riesgo", oportunidad: "Oportunidad" }} />
          {risk && <Sel label="Estado" field="estado" options={["abierto", "en_tratamiento", "mitigado", "cerrado"]} displayMap={STATUS_LABELS} />}
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Descripcion * (min 10 caracteres)</label>
            <textarea value={form.descripcion} onChange={(e) => { setForm({ ...form, descripcion: e.target.value }); setErrors(p => ({...p, descripcion: undefined})); }}
              rows={2} className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${errors.descripcion ? "border-red-400 bg-red-50" : ""}`} data-testid="risk-form-descripcion"
              placeholder="Describa el riesgo u oportunidad..." />
            {errors.descripcion && <p className="text-xs text-red-500 mt-1">{errors.descripcion}</p>}
          </div>
          <Sel label="Contexto" field="contexto" options={["interno", "externo"]} displayMap={{ interno: "Interno", externo: "Externo" }} />
          <Sel label="Origen" field="origen" options={["manual", "scanner", "auditoria", "foda", "incidente"]}
            displayMap={{ manual: "Manual", scanner: "Scanner", auditoria: "Auditoria", foda: "FODA", incidente: "Incidente" }} />
          <Sel label="Probabilidad" field="probabilidad" options={PROB_LABELS} displayMap={PROB_DISPLAY} />
          <Sel label="Impacto" field="impacto" options={IMPACT_LABELS} displayMap={IMPACT_DISPLAY_FULL} />
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Justificacion de Probabilidad</label>
            <input value={form.justificacion_probabilidad} onChange={(e) => setForm({ ...form, justificacion_probabilidad: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" data-testid="risk-form-justificacion" />
          </div>
          <Sel label="Tratamiento" field="tratamiento" options={["mitigar", "aceptar", "transferir", "evitar"]}
            displayMap={{ mitigar: "Mitigar", aceptar: "Aceptar", transferir: "Transferir", evitar: "Evitar" }} />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Responsable</label>
            <input value={form.responsable_nombre} onChange={(e) => { setForm({ ...form, responsable_nombre: e.target.value }); setErrors(p => ({...p, responsable_nombre: undefined})); }}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${errors.responsable_nombre ? "border-red-400 bg-red-50" : ""}`} data-testid="risk-form-responsable" />
            {errors.responsable_nombre && <p className="text-xs text-red-500 mt-1">{errors.responsable_nombre}</p>}
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Acciones / Planificacion</label>
            <textarea value={form.acciones} onChange={(e) => { setForm({ ...form, acciones: e.target.value }); setErrors(p => ({...p, acciones: undefined})); }}
              rows={2} className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${errors.acciones ? "border-red-400 bg-red-50" : ""}`} data-testid="risk-form-acciones" />
            {errors.acciones && <p className="text-xs text-red-500 mt-1">{errors.acciones}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Objetivo</label>
            <input type="date" value={form.fecha_objetivo} onChange={(e) => { setForm({ ...form, fecha_objetivo: e.target.value }); setErrors(p => ({...p, fecha_objetivo: undefined})); }}
              min={today}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${errors.fecha_objetivo ? "border-red-400 bg-red-50" : ""}`} data-testid="risk-form-fecha" />
            {errors.fecha_objetivo && <p className="text-xs text-red-500 mt-1">{errors.fecha_objetivo}</p>}
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50" data-testid="risk-form-cancel">Cancelar</button>
          <button onClick={handleSave} disabled={saving || !form.descripcion.trim() || form.descripcion.length < 10}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="risk-form-save">
            {saving ? "Guardando..." : risk ? "Actualizar" : "Crear Riesgo"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ───────────────────────────────── */

/* -- Audit Log Panel -- */
const AuditLog = ({ open, onClose, currentUser, selectedCompanyId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const token = localStorage.getItem("token");
    axios.get(API + "/risks/audit-log?limit=100" + (currentUser?.role === "superadmin" && selectedCompanyId ? `&company_id=${selectedCompanyId}` : ""), { headers: { Authorization: "Bearer " + token } })
      .then((res) => setLogs(res.data.logs || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [open]);

  const actionLabels = { crear: "Creado", editar: "Editado", eliminar: "Eliminado", importar: "Importado" };
  const actionColors = {
    crear: "bg-green-100 text-green-800",
    editar: "bg-blue-100 text-blue-800",
    eliminar: "bg-red-100 text-red-800",
    importar: "bg-purple-100 text-purple-800",
  };

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" }) + " " + d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="audit-log-dialog">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Historial de Auditoria</h2>
            <p className="text-sm text-gray-500 mt-0.5">Registro de acciones sobre riesgos (ISO 27001)</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-10"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>
          ) : logs.length === 0 ? (
            <p className="text-center text-gray-400 py-10">No hay registros de auditoria.</p>
          ) : (
            <div className="space-y-2">
              {logs.map((log, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <span className={"inline-block px-2 py-0.5 rounded text-xs font-semibold mt-0.5 shrink-0 " + (actionColors[log.action] || "bg-gray-100 text-gray-800")}>
                    {actionLabels[log.action] || log.action}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800">{log.risk_descripcion || "Sin descripcion"}</p>
                    {log.details && log.details.campos && (
                      <p className="text-xs text-gray-400 mt-0.5">Campos: {log.details.campos.join(", ")}</p>
                    )}
                    {log.details && log.details.cantidad && (
                      <p className="text-xs text-gray-400 mt-0.5">{log.details.cantidad} importados, {log.details.omitidas || 0} omitidos</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-gray-500">{log.user_name}</p>
                    <p className="text-[10px] text-gray-400">{formatDate(log.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cerrar</button>
        </div>
      </div>
    </div>
  );
};

const RiskManagement = () => {

  // Company selector for superadmin
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [moduleStatus, setModuleStatus] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setCurrentUser(res.data);
        if (res.data.role === "superadmin") {
          axios.get(`${API}/admin/companies`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => setCompanies(r.data.filter(c => c.name !== "CostillaTech.com Platform" && c.name !== "CostillaTech.com")))
            .catch(() => {});
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedCompanyId || !currentUser || currentUser.role !== "superadmin") {
      setModuleStatus(null);
      return;
    }
    const token = localStorage.getItem("token");
    axios.get(`${API}/admin/company-modules/${selectedCompanyId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setModuleStatus(res.data))
      .catch(() => setModuleStatus(null));
  }, [selectedCompanyId, currentUser]);



  const [risks, setRisks] = useState([]);
  const [stats, setStats] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLevel, setFilterLevel] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(API + "/risks/export/pdf", {
        headers: { Authorization: "Bearer " + token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Reporte_Riesgos_ISO27001_" + new Date().toISOString().split("T")[0] + ".pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Error al exportar PDF"); }
  };

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const h = { Authorization: `Bearer ${token}` };
      const [rr, sr, mr] = await Promise.all([
        axios.get(`${API}/risks` + (currentUser?.role === "superadmin" && selectedCompanyId ? `?company_id=${selectedCompanyId}` : ""), { headers: h }),
        axios.get(`${API}/risks/stats` + (currentUser?.role === "superadmin" && selectedCompanyId ? `?company_id=${selectedCompanyId}` : ""), { headers: h }),
        axios.get(`${API}/risks/matrix` + (currentUser?.role === "superadmin" && selectedCompanyId ? `?company_id=${selectedCompanyId}` : ""), { headers: h }),
      ]);
      setRisks(rr.data.risks || []); setStats(sr.data); setMatrix(mr.data);
    } catch (err) { console.error("Error:", err); }
    finally { setLoading(false); }
  }, [selectedCompanyId, currentUser]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Seguro que desea eliminar este riesgo?")) return;
    try { const t = localStorage.getItem("token"); await axios.delete(`${API}/risks/${id}`, { headers: { Authorization: `Bearer ${t}` } }); fetchData(); }
    catch { alert("Error al eliminar"); }
  };

  const filtered = risks.filter((r) => {
    if (filterStatus !== "all" && r.estado !== filterStatus) return false;
    if (filterLevel !== "all" && r.nivel_riesgo_texto !== filterLevel) return false;
    if (searchTerm && !r.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) && !r.responsable_nombre?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="risk-loading"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="risk-management-page">
      {/* Company Selector for SuperAdmin */}
      {currentUser?.role === "superadmin" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-red-800 mb-2">Filtrar por Empresa</h3>
          <select data-testid="risk-company-selector" value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 bg-white text-sm">
            <option value="">Todas las empresas (consolidado)</option>
            {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          {selectedCompanyId && (
            <div className="mt-2">
              <p className="text-xs text-red-600">Mostrando: <strong>{companies.find(c => c.id === selectedCompanyId)?.name}</strong></p>
              {moduleStatus && !moduleStatus["risk_management"] && (
                <div className="mt-2 bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-start gap-2">
                  <svg className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Modulo Gestion de Riesgos no habilitado</p>
                    <p className="text-xs text-yellow-600 mt-0.5">Este modulo no esta activo para esta empresa. Puede habilitarlo desde el Panel de Administracion.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Gestion de Riesgos</h1>
          <p className="text-xs text-gray-500">ISO 27001 - Matriz de Contexto y Riesgos</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAuditOpen(true)} className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-testid="audit-log-btn">
            <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="10"/></svg>
              Auditoria
            </span>
          </button>
          <button onClick={handleExportPDF} className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-testid="export-pdf-btn">
            <span style={{display:'inline-flex',alignItems:'center',gap:4}}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              PDF ISO 27001
            </span>
          </button>
          <button onClick={() => setImportOpen(true)} className="px-3 py-1.5 text-sm border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50" data-testid="import-from-scans-btn">Importar desde Escaneos</button>
          <button onClick={() => { setEditingRisk(null); setDialogOpen(true); }} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-testid="create-risk-btn">+ Nuevo Riesgo</button>
        </div>
      </div>

      {/* Stats Row + Heat Map — single bg card */}
      {stats && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Left: KPIs + level bars */}
            <div className="flex-1 min-w-0">
              {/* KPI numbers */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
                {[
                  { l: "Total", v: stats.total, c: "text-slate-700" },
                  { l: "Abiertos", v: stats.by_status?.abierto || 0, c: "text-blue-600" },
                  { l: "En Tratam.", v: stats.by_status?.en_tratamiento || 0, c: "text-orange-600" },
                  { l: "Criticos", v: stats.by_level?.critico || 0, c: "text-red-600" },
                  { l: "Altos", v: stats.by_level?.alto || 0, c: "text-orange-600" },
                  { l: "Cerrados", v: stats.by_status?.cerrado || 0, c: "text-green-600" },
                ].map((k) => (
                  <div key={k.l} className="text-center">
                    <p className={`text-2xl font-bold ${k.c}`}>{k.v}</p>
                    <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{k.l}</p>
                  </div>
                ))}
              </div>
              {/* Level distribution bars */}
              <div className="space-y-1.5">
                {[
                  { key: "critico", label: "Critico", color: "bg-red-500" },
                  { key: "alto", label: "Alto", color: "bg-orange-400" },
                  { key: "medio", label: "Medio", color: "bg-yellow-400" },
                  { key: "bajo", label: "Bajo", color: "bg-green-400" },
                ].map((lv) => {
                  const count = stats.by_level?.[lv.key] || 0;
                  const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
                  return (
                    <div key={lv.key} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-14 text-right">{lv.label}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                        <div className={`${lv.color} h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-gray-600 w-6 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Right: Heat Map */}
            {matrix && (
              <div className="lg:w-[360px] shrink-0 lg:border-l lg:pl-4">
                <HeatMap matrix={matrix.matrix} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters + Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="risk-table-card">
        {/* Filter bar */}
        <div className="px-4 py-3 border-b flex flex-wrap gap-3 items-center">
          <input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="w-48 border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500" data-testid="risk-search" />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="filter-status">
            <option value="all">Todos los estados</option>
            <option value="abierto">Abierto</option><option value="en_tratamiento">En Tratamiento</option>
            <option value="mitigado">Mitigado</option><option value="cerrado">Cerrado</option>
          </select>
          <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)} className="border rounded-lg px-3 py-1.5 text-sm" data-testid="filter-level">
            <option value="all">Todos los niveles</option>
            <option value="critico">Critico</option><option value="alto">Alto</option>
            <option value="medio">Medio</option><option value="bajo">Bajo</option>
          </select>
          <span className="text-xs text-gray-400 ml-auto">{filtered.length} de {risks.length}</span>
        </div>
        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="risk-table">
            <thead>
              <tr className="bg-gray-50 border-b text-xs">
                <th className="text-left px-3 py-2.5 font-semibold text-gray-500 w-14">Tipo</th>
                <th className="text-left px-3 py-2.5 font-semibold text-gray-500 w-24">Estado</th>
                <th className="text-left px-3 py-2.5 font-semibold text-gray-500">Descripcion</th>
                <th className="text-left px-3 py-2.5 font-semibold text-gray-500 w-20">Prob.</th>
                <th className="text-left px-3 py-2.5 font-semibold text-gray-500 w-20">Impacto</th>
                <th className="text-left px-3 py-2.5 font-semibold text-gray-500 w-20">Nivel</th>
                <th className="text-left px-3 py-2.5 font-semibold text-gray-500 w-24">Tratam.</th>
                <th className="text-left px-3 py-2.5 font-semibold text-gray-500 w-28">Responsable</th>
                <th className="text-right px-3 py-2.5 font-semibold text-gray-500 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-10 text-gray-400 text-sm">{risks.length === 0 ? "No hay riesgos registrados." : "Sin resultados."}</td></tr>
              ) : filtered.map((r) => {
                const lc = LEVEL_COLORS[r.nivel_riesgo_texto] || LEVEL_COLORS.medio;
                return (
                  <tr key={r.id} className="border-b hover:bg-gray-50/50 transition-colors" data-testid={`risk-row-${r.id}`}>
                    <td className="px-3 py-2.5">
                      <span className={`inline-block w-6 text-center py-0.5 rounded text-[11px] font-semibold ${r.tipo === "riesgo" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{r.tipo === "riesgo" ? "R" : "O"}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_COLORS[r.estado] || "bg-gray-100"}`}>{STATUS_LABELS[r.estado] || r.estado}</span>
                    </td>
                    <td className="px-3 py-2.5 max-w-xs"><p className="truncate text-gray-800 text-[13px]" title={r.descripcion}>{r.descripcion}</p></td>
                    <td className="px-3 py-2.5 text-[11px]">{PROB_DISPLAY[r.probabilidad] || r.probabilidad}</td>
                    <td className="px-3 py-2.5 text-[11px]">{IMPACT_DISPLAY_FULL[r.impacto] || r.impacto}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-bold border ${lc.bg} ${lc.text} ${lc.border}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${lc.dot}`} />
                        {(r.nivel_riesgo_texto || "").charAt(0).toUpperCase() + (r.nivel_riesgo_texto || "").slice(1)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[11px] capitalize">{r.tratamiento}</td>
                    <td className="px-3 py-2.5 text-[11px] text-gray-500 truncate max-w-[110px]">{r.responsable_nombre || "-"}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex gap-0.5 justify-end">
                        <button onClick={() => { setEditingRisk(r); setDialogOpen(true); }} className="p-1 rounded hover:bg-blue-50 text-blue-600" title="Editar" data-testid={`edit-risk-${r.id}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                        </button>
                        <button onClick={() => handleDelete(r.id)} className="p-1 rounded hover:bg-red-50 text-red-500" title="Eliminar" data-testid={`delete-risk-${r.id}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <RiskFormDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingRisk(null); }} onSave={fetchData} risk={editingRisk} />
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImport={fetchData} currentUser={currentUser} selectedCompanyId={selectedCompanyId} />
      <AuditLog open={auditOpen} onClose={() => setAuditOpen(false)} currentUser={currentUser} selectedCompanyId={selectedCompanyId} />
    </div>
  );
};

export default RiskManagement;
